/* eslint-disable */
import React from 'react';
import SEO from './SEO';
import './styles/styles.css';

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'Bins Storage – Tacoma, WA',
  description:
    'On-demand valet storage in Tacoma, Washington. We pick up, store, and deliver your belongings – no truck, no hassle. Serving Tacoma, Lakewood, University Place, Puyallup & the South Sound.',
  url: 'https://www.bins-storage.com/tacoma',
  areaServed: [
    { '@type': 'City', name: 'Tacoma' },
    { '@type': 'City', name: 'Lakewood' },
    { '@type': 'City', name: 'University Place' },
    { '@type': 'City', name: 'Puyallup' },
    { '@type': 'AdministrativeArea', name: 'Pierce County' },
  ],
  priceRange: '$$',
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.bins-storage.com/' },
    { '@type': 'ListItem', position: 2, name: 'Tacoma', item: 'https://www.bins-storage.com/tacoma' },
  ],
};

const tacomaFaqs = [
  {
    q: 'When did Bins launch in Tacoma?',
    a: 'April 2026. We\'re actively onboarding new facilities and expanding coverage across the South Sound.',
  },
  {
    q: 'Do you serve JBLM (Joint Base Lewis-McChord)?',
    a: 'Yes – JBLM families are a core part of our Tacoma launch. We can accommodate PCS timelines and deployment-related storage needs. Contact us for anything special.',
  },
  {
    q: 'How far in advance should I book a Tacoma pickup?',
    a: 'We recommend 2–3 days out during our initial launch period. Same-day and next-day slots will be more available as we scale.',
  },
  {
    q: 'Where are your storage facilities in Tacoma?',
    a: 'Across the South Sound. You\'ll see exact options during signup, along with unit sizes, pricing, and any moving fees.',
  },
  {
    q: 'Can I pick up my items from the facility directly?',
    a: 'Generally no – our team handles everything to keep your items secure. In certain cases we can accommodate it. Contact us.',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: tacomaFaqs.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

const combinedSchema = [localBusinessSchema, breadcrumbSchema, faqSchema];

// ─── Styles ───────────────────────────────────────────────────────────────────

const heroStyle = {
  backgroundColor: '#261136',
  padding: '4rem 2rem 3rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
};

const launchBadge = {
  display: 'inline-block',
  backgroundColor: 'rgba(255,255,255,0.15)',
  color: '#FFF',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '0.85rem',
  padding: '6px 16px',
  borderRadius: 999,
  marginBottom: '1rem',
  border: '1px solid rgba(255,255,255,0.3)',
  letterSpacing: '0.05em',
};

const heroHeadingStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: 'clamp(2rem, 5vw, 3.5rem)',
  fontWeight: 700,
  color: '#FFF',
  margin: '0 0 1rem',
};

const heroSubStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1.2rem',
  color: 'rgba(255,255,255,0.88)',
  maxWidth: 620,
  lineHeight: 1.6,
  margin: '0 0 0.5rem',
};

const ctaRow = {
  display: 'flex',
  gap: '1rem',
  flexWrap: 'wrap',
  justifyContent: 'center',
  margin: '1.5rem 0 0.75rem',
};

const ctaPrimary = {
  display: 'inline-block',
  backgroundColor: '#7B1FA2',
  color: '#FFF',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1.1rem',
  padding: '14px 32px',
  borderRadius: 999,
  textDecoration: 'none',
};

const ctaSecondary = {
  display: 'inline-block',
  backgroundColor: 'transparent',
  color: '#FFF',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1.1rem',
  padding: '14px 32px',
  borderRadius: 999,
  textDecoration: 'none',
  border: '2px solid rgba(255,255,255,0.4)',
};

const trustLine = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.9rem',
  color: 'rgba(255,255,255,0.65)',
  margin: '0.5rem 0 0',
};

const sectionStyle = {
  padding: '3.5rem 2rem',
  maxWidth: 900,
  margin: '0 auto',
};

const sectionHeadingStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '2rem',
  fontWeight: 700,
  color: '#261136',
  marginBottom: '0.5rem',
  textAlign: 'center',
};

const cardsGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '1.5rem',
};

const card = {
  backgroundColor: '#F9F6FC',
  borderRadius: 12,
  padding: '1.75rem 1.5rem',
  textAlign: 'center',
};

const cardTitle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1.1rem',
  fontWeight: 700,
  color: '#261136',
  marginBottom: '0.5rem',
};

const cardBody = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.95rem',
  color: '#555',
  lineHeight: 1.6,
};

const stepNumber = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '2.5rem',
  fontWeight: 800,
  color: '#7B1FA2',
  marginBottom: '0.5rem',
};

const areaSection = {
  backgroundColor: '#F9F6FC',
  padding: '3rem 2rem',
  textAlign: 'center',
};

const areaGrid = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.75rem',
  justifyContent: 'center',
  maxWidth: 700,
  margin: '1.5rem auto 0',
};

const areaBadge = {
  backgroundColor: '#EDE0F7',
  color: '#5B1F8A',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 600,
  fontSize: '0.9rem',
  padding: '8px 18px',
  borderRadius: 999,
};

const pricingSection = {
  backgroundColor: '#7B1FA2',
  padding: '3.5rem 2rem',
  textAlign: 'center',
};

const pricingCtaStyle = {
  display: 'inline-block',
  backgroundColor: '#FFF',
  color: '#7B1FA2',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1rem',
  padding: '12px 28px',
  borderRadius: 999,
  textDecoration: 'none',
};

const faqSectionStyle = {
  padding: '3.5rem 2rem',
  maxWidth: 720,
  margin: '0 auto',
};

const faqItem = {
  borderBottom: '1px solid #EEE',
  paddingBottom: '1.25rem',
  marginBottom: '1.25rem',
};

const faqQuestion = {
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1.05rem',
  color: '#261136',
  marginBottom: '0.4rem',
};

const faqAnswer = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.97rem',
  color: '#555',
  lineHeight: 1.65,
};

const finalCtaSection = {
  backgroundColor: '#261136',
  padding: '4rem 2rem',
  textAlign: 'center',
};

const finalCtaHeading = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: 'clamp(1.5rem, 4vw, 2.25rem)',
  fontWeight: 700,
  color: '#FFF',
  margin: '0 0 0.75rem',
};

const finalCtaSub = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1.1rem',
  color: 'rgba(255,255,255,0.8)',
  margin: '0 0 2rem',
  textAlign: 'center',
};

const internalLinksSection = {
  backgroundColor: '#F9F6FC',
  padding: '2rem',
  textAlign: 'center',
};

const internalLinkStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.95rem',
  color: '#7B1FA2',
  margin: '0 0.75rem',
  textDecoration: 'underline',
};

// ─── Component ────────────────────────────────────────────────────────────────

const Tacoma = () => (
  <>
    <SEO
      title="Valet Storage in Tacoma, WA | Pickup & Delivery | Bins Storage"
      description="On-demand valet storage is here in Tacoma, Washington. We pick up, store, and deliver your belongings – no truck rental, no heavy lifting. Reserve in minutes."
      path="/tacoma"
      schema={combinedSchema}
    />

    {/* HERO */}
    <section style={heroStyle}>
      <span style={launchBadge}>NEW: Now Serving Tacoma</span>
      <h1 style={heroHeadingStyle}>Valet Storage in Tacoma, WA</h1>
      <p style={heroSubStyle}>
        Bins has officially launched in Tacoma. We pick it up, store it, and
        bring it back – so you don't have to deal with a truck or a storage key.
      </p>
      <div style={ctaRow}>
        <a href="/Download" style={ctaPrimary} data-cta="tacoma-hero-reserve">
          Reserve Now
        </a>
        <a href="#how-it-works" style={ctaSecondary} data-cta="tacoma-hero-hiw">
          See How It Works
        </a>
      </div>
      <p style={trustLine}>
        Serving Tacoma, Lakewood, University Place, Puyallup &amp; the South Sound.
      </p>
    </section>

    {/* BENEFITS */}
    <section style={{ backgroundColor: '#FFF', padding: '3.5rem 2rem' }}>
      <div style={sectionStyle}>
        <h2 style={sectionHeadingStyle}>Full-Service Storage, Now in the South Sound</h2>
        <div style={cardsGrid}>
          <div style={card}>
            <div style={cardTitle}>🚚 Pickup at Your Door</div>
            <div style={cardBody}>
              From North End bungalows to Ruston waterfront condos to Lakewood
              apartments, our Tacoma team comes to you.
            </div>
          </div>
          <div style={card}>
            <div style={cardTitle}>🌡 Climate-Controlled Facilities</div>
            <div style={cardBody}>
              Every facility in our Tacoma network is climate-controlled, insured,
              and 24/7 monitored.
            </div>
          </div>
          <div style={card}>
            <div style={cardTitle}>✅ No Truck, No Hassle</div>
            <div style={cardBody}>
              Skip the U-Haul. We handle the loading, driving, and storing.
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* HOW IT WORKS */}
    <section id="how-it-works" style={{ backgroundColor: '#F9F6FC', padding: '3.5rem 2rem' }}>
      <div style={sectionStyle}>
        <h2 style={sectionHeadingStyle}>How Bins Works in Tacoma</h2>
        <div style={cardsGrid}>
          {[
            { n: '1', t: 'Reserve online', b: 'Pick your facility, unit size, and pickup window.' },
            { n: '2', t: 'We pick up', b: 'Our Tacoma team arrives at your home, loads your items, and takes them to a secure South Sound facility.' },
            { n: '3', t: 'We store it', b: 'Climate-controlled. Monitored 24/7. Fully insured.' },
            { n: '4', t: 'We deliver it back', b: 'Schedule individual items or your whole unit to come back whenever you need it.' },
          ].map(({ n, t, b }) => (
            <div key={n} style={card}>
              <div style={stepNumber}>{n}</div>
              <div style={cardTitle}>{t}</div>
              <div style={cardBody}>{b}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* AREAS */}
    <section style={areaSection}>
      <h2 style={{ ...sectionHeadingStyle, marginBottom: '0.5rem' }}>
        South Sound Neighborhoods We Serve
      </h2>
      <div style={areaGrid}>
        {[
          'North End', 'Hilltop', 'Proctor', 'Stadium District', 'Ruston', 'Dome District',
          'Lakewood', 'University Place', 'Fircrest',
          'Puyallup', 'Sumner',
          'Federal Way (on request)', 'Gig Harbor (on request)',
        ].map((area) => (
          <span key={area} style={areaBadge}>{area}</span>
        ))}
      </div>
      <p style={{ fontFamily: 'Raleway, sans-serif', fontSize: '0.95rem', color: '#666', marginTop: '1.5rem' }}>
        Don't see your area?{' '}
        <a href="/Contact" style={{ color: '#7B1FA2' }}>Contact us</a> – we're expanding fast.
      </p>
    </section>

    {/* WHO WE SERVE */}
    <section style={{ backgroundColor: '#FFF', padding: '3.5rem 2rem' }}>
      <div style={sectionStyle}>
        <h2 style={sectionHeadingStyle}>Who We Serve in Tacoma</h2>
        <div style={cardsGrid}>
          <div style={card}>
            <div style={cardTitle}>🎓 UW Tacoma &amp; PLU Students</div>
            <div style={cardBody}>
              Summer storage, dorm move-out, study abroad.{' '}
              <a href="/students" style={{ color: '#7B1FA2' }}>See student storage →</a>
            </div>
          </div>
          <div style={card}>
            <div style={cardTitle}>🏠 Apartment Renters</div>
            <div style={cardBody}>
              Free up closet space without a truck.{' '}
              <a href="/apartment-storage" style={{ color: '#7B1FA2' }}>See apartment storage →</a>
            </div>
          </div>
          <div style={card}>
            <div style={cardTitle}>🎖 Military Families at JBLM</div>
            <div style={cardBody}>
              PCS moves, deployments, and temporary storage between assignments.
            </div>
          </div>
          <div style={card}>
            <div style={cardTitle}>🔨 Homeowners Mid-Renovation</div>
            <div style={cardBody}>
              Store furniture and boxes while your space is being worked on.
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* PRICING */}
    <section style={pricingSection}>
      <h2 style={{ fontFamily: 'Raleway, sans-serif', fontSize: '2rem', fontWeight: 700, color: '#FFF', marginBottom: '0.75rem' }}>
        Tacoma Storage Pricing
      </h2>
      <p style={{ fontFamily: 'Raleway, sans-serif', fontSize: '1.05rem', color: 'rgba(255,255,255,0.85)', maxWidth: 560, margin: '0 auto 2rem', lineHeight: 1.6 }}>
        Tacoma pricing depends on the facility and unit size you pick. Compare moving
        fees, unit sizes, and rates across South Sound facilities in one place.
      </p>
      <a href="/Download" style={pricingCtaStyle} data-cta="tacoma-pricing-reserve">
        Compare Tacoma Pricing →
      </a>
    </section>

    {/* FAQ */}
    <section style={faqSectionStyle}>
      <h2 style={{ ...sectionHeadingStyle, textAlign: 'left', marginBottom: '1.75rem' }}>
        Tacoma FAQ
      </h2>
      {tacomaFaqs.map(({ q, a }) => (
        <div key={q} style={faqItem}>
          <div style={faqQuestion}>{q}</div>
          <div style={faqAnswer}>
            {a}{' '}
            {a.includes('Contact us') && (
              <a href="/Contact" style={{ color: '#7B1FA2' }}>Contact us</a>
            )}
          </div>
        </div>
      ))}
    </section>

    {/* FINAL CTA */}
    <section style={finalCtaSection}>
      <h2 style={finalCtaHeading}>Tacoma's Easier Storage Option Is Here.</h2>
      <p style={finalCtaSub}>Reserve in minutes. We handle the rest.</p>
      <a href="/Download" style={{ ...ctaPrimary, backgroundColor: '#7B1FA2' }} data-cta="tacoma-final-reserve">
        Reserve Now
      </a>
    </section>

    {/* INTERNAL LINKS */}
    <section style={internalLinksSection}>
      <p style={{ fontFamily: 'Raleway, sans-serif', color: '#666', fontSize: '0.9rem', margin: '0 0 0.75rem', textAlign: 'center' }}>
        Explore more
      </p>
      <a href="/san-francisco" style={internalLinkStyle}>SF Bay Area Storage</a>
      <a href="/students" style={internalLinkStyle}>Student Storage</a>
      <a href="/apartment-storage" style={internalLinkStyle}>Apartment Storage</a>
      <a href="/FAQ" style={internalLinkStyle}>FAQ</a>
      <a href="/About" style={internalLinkStyle}>About</a>
    </section>
  </>
);

export default Tacoma;
